import React, {useState, useEffect, useCallback} from 'react';
import {
    CurrencyPoundIcon,
    CheckCircleIcon,
    InformationCircleIcon,
    TagIcon,
    PlusIcon,
    TrashIcon,
    PencilSquareIcon
} from '@heroicons/react/24/outline';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

const VenueTokenSettings = () => {
    const [venueKey, setVenueKey] = useState('');
    const [tokenPrice, setTokenPrice] = useState('');
    const [currentPrice, setCurrentPrice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');

    // Discount tiers
    const [discountTiers, setDiscountTiers] = useState([]);
    const [discountError, setDiscountError] = useState('');
    const [discountSuccess, setDiscountSuccess] = useState('');
    const [editingTier, setEditingTier] = useState(null); // null = adding new
    const [showTierForm, setShowTierForm] = useState(false);
    const [tierForm, setTierForm] = useState({ id: null, minQuantity: '', discountPercent: '' });
    const [savingTier, setSavingTier] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const fetchAll = useCallback( async () => {
        setLoading(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const [priceRes, discountRes] = await Promise.all([
                fetch(`${baseUrl}/api/Venue/${venueKey}/token-price`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    credentials: 'include'
                }),
                fetch(`${baseUrl}/api/Venue/${venueKey}/token-discounts`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    credentials: 'include'
                })
            ]);

            if (priceRes.ok) {
                const data = await priceRes.json();
                setCurrentPrice(data.tokenPrice);
                setTokenPrice(data.tokenPrice != null ? String(data.tokenPrice) : '');
            } else {
                setError('Failed to load current token price');
            }

            if (discountRes.ok) {
                const tiers = await discountRes.json();
                setDiscountTiers(tiers);
            }
        } catch (err) {
            console.error('Error fetching token settings:', err);
            setError('Failed to load token settings');
        } finally {
            setLoading(false);
        }
    }, [venueKey]);
    
    useEffect(() => {
        const init = async () => {
            const key = await Helper.getUserVenueId();
            if (key) setVenueKey(key);
        };
        init();
    }, []);

    useEffect(() => {
        if (!venueKey) return;
        fetchAll();
    }, [venueKey, fetchAll]);

    

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        const price = parseFloat(tokenPrice);
        if (isNaN(price) || price <= 0) {
            setError('Please enter a valid price greater than zero');
            return;
        }

        setSaving(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Venue/${venueKey}/token-price`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ tokenPrice: price })
            });

            if (response.ok) {
                setCurrentPrice(price);
                setSuccessMessage('Token price updated successfully');
                setTimeout(() => setSuccessMessage(''), 4000);
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.message || 'Failed to update token price');
            }
        } catch (err) {
            setError('Failed to save token price. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openNewTierForm = () => {
        setTierForm({ id: null, minQuantity: '', discountPercent: '' });
        setEditingTier(null);
        setDiscountError('');
        setShowTierForm(true);
    };

    const openEditTierForm = (tier) => {
        setTierForm({ id: tier.id, minQuantity: String(tier.minQuantity), discountPercent: String(tier.discountPercent) });
        setEditingTier(tier.id);
        setDiscountError('');
        setShowTierForm(true);
    };

    const handleSaveTier = async (e) => {
        e.preventDefault();
        setDiscountError('');

        const minQty = parseInt(tierForm.minQuantity, 10);
        const discPct = parseInt(tierForm.discountPercent, 10);

        if (isNaN(minQty) || minQty < 1) {
            setDiscountError('Minimum quantity must be at least 1.');
            return;
        }
        if (isNaN(discPct) || discPct < 5 || discPct > 20) {
            setDiscountError('Discount percent must be between 5 and 20.');
            return;
        }

        setSavingTier(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Venue/${venueKey}/token-discounts`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ id: tierForm.id || 0, minQuantity: minQty, discountPercent: discPct })
            });

            if (response.ok) {
                setDiscountSuccess(editingTier ? 'Discount tier updated.' : 'Discount tier added.');
                setTimeout(() => setDiscountSuccess(''), 4000);
                setShowTierForm(false);
                await fetchAll();
            } else {
                const data = await response.json().catch(() => ({}));
                setDiscountError(data.message || 'Failed to save discount tier.');
            }
        } catch (err) {
            setDiscountError('Failed to save discount tier.');
        } finally {
            setSavingTier(false);
        }
    };

    const handleDeleteTier = async (discountId) => {
        if (!window.confirm('Delete this discount tier?')) return;
        setDeletingId(discountId);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Venue/${venueKey}/token-discounts/${discountId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include'
            });
            if (response.ok) {
                setDiscountTiers(prev => prev.filter(t => t.id !== discountId));
                setDiscountSuccess('Discount tier deleted.');
                setTimeout(() => setDiscountSuccess(''), 4000);
            } else {
                setDiscountError('Failed to delete discount tier.');
            }
        } catch {
            setDiscountError('Failed to delete discount tier.');
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return (
            <LayoutWrapper title="Token Settings">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="Token Settings"
            subtitle="Set the price members pay per token and configure volume discounts"
        >
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Current price banner */}
                <div className="card p-6 bg-gradient-to-br from-emerald-900/30 to-gray-900">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-300 text-sm font-medium">Current Token Price</p>
                            <p className="text-4xl font-bold text-emerald-400 mt-1">
                                £{currentPrice != null ? Number(currentPrice).toFixed(2) : '—'}
                            </p>
                            <p className="text-gray-500 text-sm mt-1">per token</p>
                        </div>
                        <CurrencyPoundIcon className="h-16 w-16 text-emerald-400 opacity-30" />
                    </div>
                </div>

                {/* Update price form */}
                <div className="card p-6">
                    <h3 className="text-white font-semibold mb-4">Update Token Price</h3>

                    {successMessage && (
                        <div className="mb-4 p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg flex items-center gap-2 text-emerald-400">
                            <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                            {successMessage}
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-red-400">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSave} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Token Price (£)</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">£</span>
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={tokenPrice}
                                    onChange={(e) => setTokenPrice(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full pl-8 pr-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                    required
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                This is the amount members will pay for each token they purchase
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={saving}
                            className="w-full py-3 rounded-lg font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {saving ? 'Saving...' : 'Save Token Price'}
                        </button>
                    </form>
                </div>

                {/* Volume Discount Tiers */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <TagIcon className="h-5 w-5 text-yellow-400" />
                            <h3 className="text-white font-semibold">Volume Discount Tiers</h3>
                        </div>
                        {!showTierForm && (
                            <button
                                onClick={openNewTierForm}
                                className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white transition-colors"
                            >
                                <PlusIcon className="h-4 w-4" />
                                Add Tier
                            </button>
                        )}
                    </div>

                    <p className="text-gray-400 text-sm mb-4">
                        Offer members a percentage discount when they buy in bulk. Discount must be between <strong className="text-gray-300">5% and 20%</strong>.
                        The best applicable tier is automatically applied at checkout.
                    </p>

                    {discountSuccess && (
                        <div className="mb-4 p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg flex items-center gap-2 text-emerald-400">
                            <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                            {discountSuccess}
                        </div>
                    )}
                    {discountError && (
                        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-red-400">
                            {discountError}
                        </div>
                    )}

                    {/* Add / Edit form */}
                    {showTierForm && (
                        <form onSubmit={handleSaveTier} className="mb-4 p-4 bg-gray-800/60 rounded-lg space-y-3 border border-gray-700">
                            <h4 className="text-white text-sm font-medium">
                                {editingTier ? 'Edit Discount Tier' : 'New Discount Tier'}
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Min. Tokens</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={tierForm.minQuantity}
                                        onChange={e => setTierForm(f => ({ ...f, minQuantity: e.target.value }))}
                                        placeholder="e.g. 10"
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Discount % (5–20)</label>
                                    <input
                                        type="number"
                                        min="5"
                                        max="20"
                                        value={tierForm.discountPercent}
                                        onChange={e => setTierForm(f => ({ ...f, discountPercent: e.target.value }))}
                                        placeholder="e.g. 10"
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                                <button
                                    type="submit"
                                    disabled={savingTier}
                                    className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
                                >
                                    {savingTier ? 'Saving...' : (editingTier ? 'Update Tier' : 'Add Tier')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowTierForm(false); setDiscountError(''); }}
                                    className="px-4 py-2 rounded-lg text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Tier list */}
                    {discountTiers.length === 0 && !showTierForm ? (
                        <p className="text-gray-500 text-sm text-center py-4">
                            No discount tiers configured. Click <strong>Add Tier</strong> to create one.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {[...discountTiers]
                                .sort((a, b) => a.minQuantity - b.minQuantity)
                                .map(tier => (
                                    <div
                                        key={tier.id}
                                        className="flex items-center justify-between p-3 bg-gray-800/50 border border-gray-700 rounded-lg"
                                    >
                                        <div>
                                            <span className="text-white font-medium">
                                                Buy {tier.minQuantity}+ tokens
                                            </span>
                                            <span className="ml-3 text-sm text-yellow-400 font-bold">
                                                {tier.discountPercent}% OFF
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => openEditTierForm(tier)}
                                                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                                                title="Edit"
                                            >
                                                <PencilSquareIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTier(tier.id)}
                                                disabled={deletingId === tier.id}
                                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-700 transition-colors disabled:opacity-40"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Info box */}
                <div className="card p-4 bg-gray-800/50">
                    <div className="flex items-start gap-3">
                        <InformationCircleIcon className="h-5 w-5 text-sky-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-400 space-y-1">
                            <p>Members will see the token price and any applicable volume discounts on the <strong className="text-gray-300">Buy Tokens</strong> page.</p>
                            <p>Payments are processed securely via GoCardless. Tokens are credited once payment is confirmed.</p>
                            <p>The minimum charge is <strong className="text-gray-300">£1.00</strong> — any discount tier that would result in a lower total will be rejected at checkout.</p>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutWrapper>
    );
};

export default VenueTokenSettings;
